import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowForward as ArrowForwardIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import type { Service, Tenant } from '../../lib/db/types';
import { getPublicBookingCatalog } from '../../lib/bookingApi';

interface ServiceWithId extends Service {
  id: string;
}

interface TenantWithId extends Tenant {
  id: string;
}

interface ProviderInfo {
  id: string;
  name: string;
  photoUrl?: string;
  bio?: string;
}

const isProviderlessService = (service: ServiceWithId) =>
  service.bookingMode === 'class_session';

export function BookingProviderPage() {
  const { tenantSlug, serviceId } = useParams<{ tenantSlug: string; serviceId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tenant, setTenant] = useState<TenantWithId | null>(null);
  const [service, setService] = useState<ServiceWithId | null>(null);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch tenant and service
  useEffect(() => {
    if (!tenantSlug || !serviceId) {
      setError('Invalid appointment link');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const catalog = await getPublicBookingCatalog(tenantSlug);
        const tenantData = catalog.tenant as TenantWithId;
        setTenant(tenantData);

        const serviceData = catalog.services.find((candidate) => candidate.id === serviceId) as ServiceWithId | undefined;
        if (!serviceData) {
          setError('Service not found');
          setLoading(false);
          return;
        }

        setService(serviceData);

        if (isProviderlessService(serviceData)) {
          navigate(`/book/${tenantSlug}/service/${serviceId}/provider/class`, { replace: true });
          return;
        }

        const providersData = catalog.providers
          .filter((provider) => serviceData.providerIds?.includes(provider.id))
          .map((provider) => ({ ...provider, bio: undefined }));
        setProviders(providersData);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load appointment information');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantSlug, serviceId]);

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const formatPrice = (price?: number, currency?: string): string => {
    if (price === undefined || price === null) return 'Free';
    const dollars = price / 100;
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    return `${currencySymbol}${dollars.toFixed(2)}`;
  };

  const handleSelectProvider = (providerId: string) => {
    navigate(`/book/${tenantSlug}/service/${serviceId}/provider/${providerId}`);
  };

  const handleBackToServices = () => {
    navigate(`/book/${tenantSlug}`);
  };

  // Get tenant branding color
  const primaryColor = tenant?.branding?.primaryColor || theme.palette.primary.main;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!tenant || !service) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Appointment site not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pb: 4,
      }}
    >
      {/* Header with Tenant Branding */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {/* Tenant Logo */}
            {tenant.branding?.logoUrl ? (
              <Box
                component="img"
                src={tenant.branding.logoUrl}
                alt={tenant.name}
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: 1,
                  bgcolor: primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 'bold',
                }}
              >
                {tenant.name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight={600}
                sx={{ color: 'text.primary' }}
              >
                {tenant.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Book an appointment
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToServices}
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: primaryColor },
            }}
          >
            Services
          </Link>
          <Typography variant="body2" color="text.primary">
            Select Provider
          </Typography>
        </Breadcrumbs>

        {/* Service Summary */}
        <Card
          sx={{
            mb: 4,
            borderLeft: 4,
            borderColor: service.color || primaryColor,
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              {service.name}
            </Typography>
            
            {service.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {service.description}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                }}
              >
                <AccessTimeIcon fontSize="small" />
                <Typography variant="body2">
                  {formatDuration(service.duration)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                }}
              >
                <AttachMoneyIcon fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                  {formatPrice(service.price, service.currency)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Page Title */}
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="h2"
          fontWeight={600}
          sx={{ mb: 3 }}
        >
          Select a Provider
        </Typography>

        {/* No Providers State */}
        {providers.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Service unavailable
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This service is not available for online appointments right now.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleBackToServices}
                sx={{ textTransform: 'none' }}
              >
                Back to Services
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Providers Grid */
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {providers.map((provider) => (
              <Grid item xs={12} sm={6} lg={4} key={provider.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() => handleSelectProvider(provider.id)}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: { xs: 2, sm: 3 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    {/* Provider Avatar */}
                    <Avatar
                      src={provider.photoUrl}
                      alt={provider.name}
                      sx={{
                        width: { xs: 80, sm: 100 },
                        height: { xs: 80, sm: 100 },
                        mb: 2,
                        bgcolor: primaryColor,
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                      }}
                    >
                      {provider.name.charAt(0).toUpperCase()}
                    </Avatar>

                    {/* Provider Name */}
                    <Typography
                      variant="h6"
                      component="h3"
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      {provider.name}
                    </Typography>

                    {provider.bio ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {provider.bio}
                      </Typography>
                    ) : (
                      <Box sx={{ flexGrow: 1 }} />
                    )}

                    {/* Select Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        mt: 'auto',
                        bgcolor: primaryColor,
                        '&:hover': {
                          bgcolor: primaryColor,
                          filter: 'brightness(0.9)',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                      onClick={(e: MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        handleSelectProvider(provider.id);
                      }}
                    >
                      Select
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          py: 3,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            Powered by{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              FIT
            </Box>
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              Stac
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default BookingProviderPage;
